import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "services/api.service";
import { LogisticsEvent } from "types/logisticsEvents";
import { useGlobalAlert } from "./useAlert";

const PAGE_SIZE = 10;

export default function useLogisticsEvents() {
  const [logisticsEvents, setLogisticsEvents] = useState<LogisticsEvent[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // Adding refs instead of states given that we don't want to re-fetch the events when the state changes
  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const pageNumberRef = useRef(1);
  const logisticsEventsRef = useRef<LogisticsEvent[]>([]);
  const notificationsLoadedAtRef = useRef(new Date().toISOString());
  const navigate = useNavigate();

  const fetchMoreLogisticsEvents = useCallback(() => {
    if (isLoadingRef.current || !hasMoreRef.current) return;

    isLoadingRef.current = true;
    setIsLoading(true);

    const pageNumber = pageNumberRef.current;
    const searchParams = new URLSearchParams({
      "page[size]": String(PAGE_SIZE),
      "page[number]": String(pageNumber),
      notifications_loaded_at: notificationsLoadedAtRef.current,
    });

    api
      .get(`/logistics_events?${searchParams.toString()}`)
      .then(({ data }) => {
        const nextEvents = data.logisticsEvents as LogisticsEvent[];
        const updated = [...logisticsEventsRef.current, ...nextEvents];

        logisticsEventsRef.current = updated;
        setLogisticsEvents(updated);
        setUnreadCount(data.unreadCount);

        pageNumberRef.current = pageNumber + 1;

        const more = nextEvents.length === PAGE_SIZE;
        hasMoreRef.current = more;
        setHasMore(more);
      })
      .finally(() => {
        isLoadingRef.current = false;
        setIsLoading(false);
      });
  }, []); // No dependencies to avoid re-fetching the events when the values changes

  const markAllAsRead = useCallback(() => {
    api.put("/logistics_events/mark_all_as_read").then(({ status }) => {
      if (status === 200) {
        window.location.reload();
      } else {
        useGlobalAlert.alertError("Failed to mark all events as read");
      }
    });
  }, []);

  const markAsRead = useCallback(
    (id: number) => {
      api
        .put(`/logistics_events/${id}`, { read: true })
        .then(({ status }) => {
          if (status === 200) {
            navigate(`/logistics/pickups/${id}`);
          } else {
            useGlobalAlert.alertError("Failed to mark event as read");
          }
        })
        .catch(() => {
          useGlobalAlert.alertError("Failed to mark event as read");
        });
    },
    [navigate],
  );

  useEffect(() => {
    fetchMoreLogisticsEvents();
  }, [fetchMoreLogisticsEvents]);

  return {
    logisticsEvents,
    unreadCount,
    loading: isLoading,
    hasMore,
    fetchMoreLogisticsEvents,
    markAsRead,
    markAllAsRead,
  };
}
